import * as m from "../../model/model";

import { CoursesResponse, ExamsQuery } from "../../api/api-model";

import { adminActions } from "./adminSlice";
import { api } from "../../api/api";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { examTransformer } from "../../pages/admin/components/AdminExams/exam.transformer";

export function handleError(e: unknown, rejectWithValue: Function) {
  console.error(e);
  return rejectWithValue(e);
}

export const loadUsersThunk = createAsyncThunk<m.FbUser[]>(
  "admin/users",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const users = await api.admin.user.getUsers();
      dispatch(adminActions.setUsers(users || null));
      return users;
    } catch (e) {
      return handleError(e, rejectWithValue);
    }
  }
);

export const loadUserThunk = createAsyncThunk<m.User, string>(
  "admin/user",
  async (uid, { dispatch, rejectWithValue }) => {
    try {
      const user = await api.admin.user.getUser(uid);
      dispatch(adminActions.setSelectedUser(user || null));
      return user;
    } catch (e) {
      return handleError(e, rejectWithValue);
    }
  }
);

export const createUserThunk = createAsyncThunk<m.FbUser, m.NewUser>(
  "admin/user/create",
  async ({ name, email }, { dispatch, rejectWithValue }) => {
    try {
      const user = await api.admin.user.createUser(name, email);
      dispatch(adminActions.setSelectedUser(user));
      return user;
    } catch (e) {
      return handleError(e, rejectWithValue);
    }
  }
);

export const deleteUserThunk = createAsyncThunk<m.FbUser[], { uid: string }>(
  "admin/user/delete",
  async ({ uid }, { dispatch, rejectWithValue }) => {
    try {
      const users = await api.admin.user.deleteUser(uid);
      dispatch(adminActions.setUsers(users));
      return users;
    } catch (e) {
      return handleError(e, rejectWithValue);
    }
  }
);

export const updateUserThunk = createAsyncThunk<m.FbUser[], { user: m.User }>(
  "admin/user/update",
  async ({ user }, { dispatch, rejectWithValue }) => {
    try {
      const users = await api.admin.user.updateUser(user);
      dispatch(adminActions.setUsers(users));
      return users;
    } catch (e) {
      return handleError(e, rejectWithValue);
    }
  }
);

export const getCoursesThunk = createAsyncThunk<CoursesResponse>(
  "admin/courses",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const courses = await api.admin.courses.getCourses();
      dispatch(adminActions.setCoursesResponse(courses || null));
      return courses;
    } catch (e) {
      return handleError(e, rejectWithValue);
    }
  }
);

export const getExamsThunk = createAsyncThunk<any[], { query: ExamsQuery }>(
  "admin/exam/list",
  async ({ query }, { dispatch, rejectWithValue }) => {
    try {
      const response = await api.admin.exam.list(query);
      dispatch(adminActions.setExamsResponse(response || null));
      return response;
    } catch (e) {
      return handleError(e, rejectWithValue);
    }
  }
);

export const getExamThunk = createAsyncThunk<m.Exam, { id: string }>(
  "admin/exam",
  async ({ id }, { dispatch, rejectWithValue }) => {
    try {
      const exm = await api.admin.exam.get(id);
      const airtable = await api.admin.exam.airtable();
      const exam = exm
        ? examTransformer.fromFirestore(
            exm,
            airtable.questions,
            airtable.sections
          )
        : null;
      dispatch(adminActions.setQuestions(airtable.questions || null));
      dispatch(adminActions.setSections(airtable.sections || null));
      dispatch(adminActions.setSelectedExam(exam));
      return exam;
    } catch (e) {
      return handleError(e, rejectWithValue);
    }
  }
);

export const getExamAirtableThunk = createAsyncThunk<any[]>(
  "admin/exam/airtable",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const airtable = await api.admin.exam.airtable();
      dispatch(adminActions.setQuestions(airtable.questions || null));
      dispatch(adminActions.setSections(airtable.sections || null));
      return airtable;
    } catch (e) {
      return handleError(e, rejectWithValue);
    }
  }
);

/* export const getTestResultsThunk = createAsyncThunk<m.Test, { id: string }>(
  "admin/prof/results",
  async ({ id }, { dispatch, rejectWithValue }) => {
    try {
      const test = await api.prof.getTestResults(id);
      dispatch(adminActions.setSelectedTest(test));
      return test;
    } catch (e) {
      return handleError(e, rejectWithValue);
    }
  }
);
*/
export const updateExamThunk = createAsyncThunk<m.Exam, Partial<m.Exam>>(
  "admin/exam/results/update",
  async (partial, { dispatch, rejectWithValue }) => {
    try {
      const updatedExam = await api.admin.exam.update(partial);
      // dispatch(adminActions.setSelectedExam(updatedExam));
      return updatedExam;
    } catch (e) {
      return handleError(e, rejectWithValue);
    }
  }
);

export const getLogsThunk = createAsyncThunk<m.Log[]>(
  "admin/logs",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const logs = await api.admin.logs.getLogs();
      dispatch(adminActions.setLogs(logs));
      return logs;
    } catch (e) {
      return handleError(e, rejectWithValue);
    }
  }
);

export const listFilesThunk = createAsyncThunk<
  m.FilesResponse,
  { prefix: string }
>("admin/files", async ({ prefix }, { dispatch, rejectWithValue }) => {
  try {
    const files = await api.admin.bucket.listFiles(prefix);
    return files;
  } catch (e) {
    return handleError(e, rejectWithValue);
  }
});

export const uploadFileThunk = createAsyncThunk<
  m.FilesResponse,
  { file: File; path: string }
>(
  "admin/file/upload",
  async ({ file, path }, { dispatch, rejectWithValue }) => {
    try {
      const files = await api.admin.bucket.uploadFile(file, path);
      return files;
    } catch (e) {
      return handleError(e, rejectWithValue);
    }
  }
);

export const deleteFileThunk = createAsyncThunk<
  m.FilesResponse,
  { path: string }
>("admin/file/delete", async ({ path }, { dispatch, rejectWithValue }) => {
  try {
    const files = await api.admin.bucket.deleteFile(path);
    console.log("deleteFileThunk", { path, files });
    return files;
  } catch (e) {
    return handleError(e, rejectWithValue);
  }
});

export const renameFileThunk = createAsyncThunk<
  m.FilesResponse,
  { oldName: string; newName: string }
>(
  "admin/file/rename",
  async ({ oldName, newName }, { dispatch, rejectWithValue }) => {
    try {
      const files = await api.admin.bucket.renameFile(oldName, newName);
      return files;
    } catch (e) {
      return handleError(e, rejectWithValue);
    }
  }
);
